import { AxiosInstance } from 'axios';
import { ServiceBaseConfig } from './Interfaces/IAccountService';
import { AuthBaseConfig } from './Interfaces/IAuthService';

export type ShareholderData = {
  id: number;
  slug: string;
  company_id: number;
  name: string;
  email: string;
  gender: string;
  date_of_birth: string;
  nationality: string;
  number_of_shares: number;
  occupation: string;
  is_director: boolean;
  is_underage: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  lexisnexis_status: string;
  document_number: string;
};

export type ShareholderAddress = {
  id: number;
  slug: string;
  shareholder_id: number;
  address: string;
  complement: string;
  postal_code: string;
  city: string;
  state: string;
  neighborhood: string;
  country: string;
  document_url: string;
  description: string | null;
  type: string;
  subject: string | null;
  message: string | null;
  status: string;
  signed: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  meta_data: {
    s3Path: string;
  };
  url: string | null;
  document_name: string | null;
};

export type ShareholderTelephone = {
  id: number;
  slug: string;
  shareholder_id: number;
  DDI: string;
  DDD: string;
  number: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

export type ShareholderPassport = {
  id: number;
  slug: string;
  shareholder_id: number;
  number: string;
  expiry_date: string;
  issuing_country: string;
  issuing_date: string;
  document_url: string;
  description: string | null;
  type: string;
  subject: string | null;
  message: string | null;
  status: string;
  signed: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  meta_data: {
    s3Path: string;
  };
  url: string | null;
  document_name: string | null;
};

export type ShareholderDocument = {
  id: number;
  slug: string;
  shareholder_id: number;
  document_name: string;
  document_url: string;
  description: string | null;
  type: string;
  subject: string | null;
  message: string | null;
  status: string;
  signed: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  meta_data: {
    s3Path: string;
  };
  url: string | null;
};

export class ShareholderService {
  public static async getMe(api: AxiosInstance, config: ServiceBaseConfig): Promise<{ data: ShareholderData }> {
    try {
      const { data } = await api.get('/shareholders/me', config);
      return data;
    } catch (error) {
      console.error('Error in ShareholderService.getMe:', error);
      throw error;
    }
  }

  public static async getMyAddress(api: AxiosInstance, config: ServiceBaseConfig): Promise<{ data: ShareholderAddress[] }> {
    try {
      const { data } = await api.get('/shareholders/me/address', config);
      return data;
    } catch (error) {
      console.error('Error in ShareholderService.getMyAddress:', error);
      throw error;
    }
  }

  public static async getMyTelephones(api: AxiosInstance, config: ServiceBaseConfig): Promise<{ data: ShareholderTelephone[] }> {
    try {
      const { data } = await api.get('/shareholders/me/telephones', config);
      return data;
    } catch (error) {
      console.error('Error in ShareholderService.getMyTelephones:', error);
      throw error;
    }
  }

  public static async getMyPassports(api: AxiosInstance, config: ServiceBaseConfig): Promise<{ data: ShareholderPassport[] }> {
    try {
      const { data } = await api.get('/shareholders/me/passports', config);
      return data;
    } catch (error) {
      console.error('Error in ShareholderService.getMyPassports:', error);
      throw error;
    }
  }

  public static async getMyDocuments(api: AxiosInstance, config: ServiceBaseConfig): Promise<{ data: ShareholderDocument[] }> {
    try {
      const { data } = await api.get('/shareholders/me/documents', config);
      return data;
    } catch (error) {
      console.error('Error in ShareholderService.getMyDocuments:', error);
      throw error;
    }
  }

  public static async createPassword(
    api: AxiosInstance,
    payload: {
      user_slug: string;
      password: string;
      password_confirmation: string;
    },
    config: AuthBaseConfig
  ): Promise<{ response: string; message: string }> {
    try {
      const { data } = await api.post('/shareholders-create-password', payload, config);
      return data;
    } catch (error) {
      console.error('Error in ShareholderService.createPassword:', error);
      throw error;
    }
  }
}
